// Phase 3.5 — Clear manual lock and re-apply matrix suggestion to a parts line.
// This is the ONLY way the system overwrites a manually-locked actual price.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { suggestedSellHybrid } from '@/lib/parts-financial/calculator';
import { useShopPricingConfig } from './useShopPricingConfig';
import type { PartsLine } from '@/hooks/parts/usePartsLines';
// CSI-P2: route best-effort manual-event INSERT failures to the
// client-error buffer (matches `useReminderTriggers` pattern from
// FIX-R132-MED-1) so audit-trail gaps surface in bug reports instead
// of silently degrading the React-Query mutation result.
import { pushClientError } from '@/utils/clientErrorBuffer';

interface Args {
  line: PartsLine;
}

export function useReapplyMatrixPricing(shopId: string | undefined) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { config } = useShopPricingConfig(shopId);

  return useMutation({
    mutationFn: async ({ line }: Args) => {
      if (!shopId) throw new Error('No shop');
      // Read shop-level quantity mode from settings.
      const { data: settings } = await (supabase as any)
        .from('shop_pricing_settings')
        .select('matrix_quantity_mode')
        .eq('shop_id', shopId)
        .maybeSingle();
      const mode: 'per_unit' | 'grouped' =
        settings?.matrix_quantity_mode === 'grouped' ? 'grouped' : 'per_unit';

      const cost = line.unit_cost == null ? 0 : Number(line.unit_cost);
      const qty = Number(line.quantity ?? 1);
      const result = suggestedSellHybrid(cost, line.category, config, {
        quantity: qty,
        quantityMode: mode,
      });

      const isGrouped = mode === 'grouped';
      const patch: Record<string, any> = {
        manual_price_locked: false,
        pricing_source: 'matrix',
        pricing_method: isGrouped ? 'matrix_grouped' : 'matrix_per_unit',
        suggested_sell_each: result.suggestedEach,
        suggested_sell_total: result.suggestedTotal,
        // Authoritative side becomes the suggested value; the trigger derives the other side.
        actual_sell_each: isGrouped ? null : result.suggestedEach,
        actual_sell_total: isGrouped ? result.suggestedTotal : null,
        // Snapshot
        quantity_snapshot: qty,
        cost_each_snapshot: cost,
        cost_total_snapshot: Number((cost * qty).toFixed(2)),
        matrix_quantity_mode_snapshot: mode,
        matrix_tier_min_snapshot: null,
        matrix_tier_max_snapshot: result.tierSnapshot?.max ?? null,
        matrix_multiplier_snapshot: result.tierSnapshot?.multiplier ?? null,
      };

      const { data, error } = await (supabase as any)
        .from('parts_lines')
        .update(patch)
        .eq('id', line.id)
        .select()
        .single();
      if (error) throw error;

      // CSI-P2: derive `is_canned` consistent with the DMC-J1 trigger.
      // Pre-CSI-P2 the manual event left this field at the column DEFAULT
      // (`false`), misclassifying canned-job parts lines and breaking
      // is_canned-based filters in `OverrideInsightsCard` /
      // `useOptiGuardSuggestion` (which set `excludeCanned: true` and
      // would have INCLUDED a canned-job override-removal in their
      // aggregate stats). Mirror the trigger's join: when `line.job_id`
      // is set, look up the parent `ticket_jobs.catalog_job_id` and
      // mark the manual event canned iff that's non-null. Best-effort
      // — a query failure falls through to `false` (matches the
      // trigger's `COALESCE(_, false)` fallback).
      let isCanned = false;
      if (line.job_id) {
        try {
          const { data: jobRow } = await (supabase as any)
            .from('ticket_jobs')
            .select('catalog_job_id')
            .eq('id', line.job_id)
            .maybeSingle();
          isCanned = !!jobRow?.catalog_job_id;
        } catch (_) {
          // Fall through to false; matches DMC-J1 trigger's COALESCE.
        }
      }

      // ────────────────────────────────────────────────────────────────────
      // CSI-P1: INTENTIONAL DUAL-EVENT DESIGN — DO NOT REMOVE THIS INSERT.
      // ────────────────────────────────────────────────────────────────────
      //
      // The preceding `parts_lines.update(patch)` call fires the DMC-J1
      // `record_parts_pricing_event` AFTER trigger (migration
      // `20260511120000_dmc_j1_record_parts_pricing_event_authority.sql`)
      // in the common case — i.e. whenever the new `actual_sell_each`
      // differs from the previous manually-locked value (which is the
      // typical reapply scenario; the trigger's change-detection gate
      // skips only when reapply happens to produce the same value as
      // the previous lock).
      //
      // The trigger event and this manual event are INTENTIONALLY
      // DISTINCT and serve DIFFERENT consumers. Both rows persist.
      //
      // TRIGGER EVENT (sparse, aggregate-analytics shape):
      //   • Consumed by `OverrideInsightsCard.tsx` (90-day delta-vs-
      //     suggested aggregation per category) and
      //     `useOptiGuardSuggestion.ts` (historical GP% averaging that
      //     drives the OptiGuard matrix-adjustment recommendation).
      //   • Sets: shop_id, parts_line_id, category, unit_cost,
      //     actual_sell, source ('manual_override' or 'system'),
      //     is_canned (derived from job linkage).
      //   • Leaves NULL: suggested_sell, event_type, previous_value,
      //     new_value, actor_user_id.
      //
      // MANUAL EVENT (this INSERT — rich, lifecycle-audit shape):
      //   • Intended for event-replay / audit / forensic surfaces that
      //     need to reconstruct "who removed which manual lock when,
      //     with what before/after values" from `parts_pricing_events`
      //     alone. Captures the lifecycle audit triad that the trigger
      //     deliberately omits.
      //   • Carries the FULL lifecycle metadata:
      //       - event_type = 'manual_override_removed'
      //       - previous_value = snapshot of pre-reapply authority +
      //         lock state (actual_sell_each / actual_sell_total /
      //         manual_price_locked)
      //       - new_value = the full patch object showing the unlock +
      //         new pricing_source/method/snapshots
      //       - actor_user_id = who clicked "reapply matrix"
      //   • Also carries a populated `suggested_sell` (the trigger
      //     leaves this NULL — the manual event is more useful for any
      //     consumer that wants to see the suggested value at the time
      //     of override removal).
      //   • Source field carries the actual matrix engine source
      //     (`'matrix'`, `'optiguard'`, `'fallback'`, `'flat'`) rather
      //     than the trigger's generic `'manual_override'`.
      //   • ALWAYS recorded: this INSERT fires regardless of whether
      //     the reapply produced a different `actual_sell_each` value
      //     than the previous lock — so the lifecycle audit trail is
      //     gap-free even in the rare same-value reapply case where
      //     the trigger's change-detection gate would skip.
      //
      // KNOWN SUB-FINDINGS (CSI-P2 backlog — out of CSI-P1 scope):
      //   1. Both events end up in `parts_pricing_events` with
      //      identical `actual_sell` + `unit_cost` for the same
      //      reapply, so `OverrideInsightsCard` and
      //      `useOptiGuardSuggestion` give 2× weight to override-
      //      removal events in their aggregate stats. Mitigation:
      //      filter consumers on `event_type IS NULL` to exclude
      //      lifecycle events from aggregate stats.
      //   2. The manual event leaves `is_canned` as the column
      //      DEFAULT (`false`) instead of deriving from
      //      `ticket_jobs.catalog_job_id`. For canned-job parts_lines
      //      this is a misclassification.
      //   3. This INSERT is `await`ed without a `.catch()`. The
      //      "best-effort" comment below predates CSI-P1 and overstates
      //      the resilience — a thrown error here propagates out of
      //      the React-Query mutation even though the parts_lines
      //      UPDATE has already committed.
      //
      // None of the sub-findings change CSI-P1 scope. They are
      // documented here so a future maintainer can see the full picture
      // when picking up CSI-P2.
      // ────────────────────────────────────────────────────────────────────
      // CSI-P2: TRUE best-effort lifecycle-event INSERT.
      // Pre-CSI-P2 this INSERT was `await`ed without a `.catch()` —
      // the comment said "best-effort" but a thrown error here would
      // have propagated out of the React-Query mutation and surfaced
      // as a mutation failure to the user, even though the
      // parts_lines UPDATE above had already committed (and the
      // DMC-J1 trigger had already recorded the sparse aggregate
      // event). The mutation should NEVER fail just because the
      // lifecycle audit row didn't write. Wrap in try/catch and route
      // the error to the client-error buffer (FIX-R132-MED-1 pattern)
      // so the audit-trail gap shows up in bug reports without
      // blocking the user-visible operation. The `catch` swallows
      // the throw deliberately — the comment block above explains the
      // dual-event design and why the lifecycle event is informational
      // rather than load-bearing for runtime correctness.
      try {
        const { error: eventError } = await (supabase as any).from('parts_pricing_events').insert({
          shop_id: shopId,
          parts_line_id: line.id,
          category: line.category,
          unit_cost: cost,
          suggested_sell: result.suggestedEach,
          actual_sell: isGrouped
            ? (qty > 0 ? Number((result.suggestedTotal / qty).toFixed(2)) : 0)
            : result.suggestedEach,
          source: result.source,
          // CSI-P2: derived above from ticket_jobs.catalog_job_id;
          // mirrors the DMC-J1 trigger's canned-job classification.
          is_canned: isCanned,
          event_type: 'manual_override_removed',
          previous_value: {
            actual_sell_each: line.actual_sell_each,
            actual_sell_total: line.actual_sell_total,
            manual_price_locked: line.manual_price_locked,
          },
          new_value: patch,
          actor_user_id: user?.id ?? null,
        });
        if (eventError) {
          // Supabase returns errors via the response object (no throw).
          throw eventError;
        }
      } catch (eventErr) {
        const errMsg =
          (eventErr as { message?: string } | undefined)?.message
          ?? (typeof eventErr === 'string' ? eventErr : JSON.stringify(eventErr ?? {}));
        console.error('[useReapplyMatrixPricing] manual lifecycle-event INSERT failed (best-effort):', eventErr);
        pushClientError({
          source: 'mutation',
          message: `[useReapplyMatrixPricing] manual_override_removed event INSERT failed: ${errMsg}`,
          extra: {
            partsLineId: line.id,
            ticketId: line.ticket_id,
            shopId,
            note: 'parts_lines UPDATE already committed; DMC-J1 trigger event likely persisted; only the lifecycle audit row was lost',
          },
          shopId: shopId ?? null,
        });
        // Deliberately swallow — see CSI-P2 doc-block above.
      }

      return data as PartsLine;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['parts-lines', row.shop_id, row.ticket_id] });
      qc.invalidateQueries({ queryKey: ['parts-pricing-events', row.shop_id] });
    },
  });
}
